import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { MacTerminal, type TerminalLine } from "../components/MacTerminal";
import {
  serviceKeys,
  type ValidationRun,
  type ValidationStep,
} from "../api/staff";

interface StreamMessage {
  type:
    | "run.started"
    | "step.running"
    | "step.done"
    | "run.finished";
  ordering?: number;
  command?: string;
  status?: "ok" | "fail";
  output?: string;
  durationMs?: number;
  outcome?: "success" | "failure";
  failureReason?: string | null;
}

export function ServiceKeyValidate() {
  const { id = "", runId = "" } = useParams();
  const [lines, setLines] = useState<TerminalLine[]>([]);
  const [banner, setBanner] = useState<
    { kind: "success" | "failure"; text: string } | undefined
  >(undefined);
  const [error, setError] = useState<string | null>(null);
  const seenStepsRef = useRef<Set<number>>(new Set());

  const append = (line: TerminalLine) => {
    setLines((prev) => [...prev, line]);
  };

  // Initial snapshot — replay any steps already persisted.
  useEffect(() => {
    let cancelled = false;
    serviceKeys
      .getRun(id, runId)
      .then((out) => {
        if (cancelled) return;
        const initial: TerminalLine[] = [
          {
            id: "boot",
            status: "info",
            text: `validator $ run --key=${id} --run=${runId}`,
          },
        ];
        for (const s of out.run.steps) {
          initial.push(stepToLine(s));
          seenStepsRef.current.add(s.ordering);
        }
        setLines(initial);
        if (out.run.outcome === "success") {
          setBanner({ kind: "success", text: "KEY VALIDATED SUCCESSFULLY" });
        } else if (out.run.outcome === "failure") {
          setBanner({
            kind: "failure",
            text: out.run.failureReason ?? "VALIDATION FAILED",
          });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load run");
      });
    return () => {
      cancelled = true;
    };
  }, [id, runId]);

  // Live updates via SSE.
  useEffect(() => {
    if (!id || !runId) return;
    const es = new EventSource(serviceKeys.streamUrl(id, runId));
    es.addEventListener("update", (ev) => {
      const msg = JSON.parse((ev as MessageEvent).data) as StreamMessage;
      if (msg.type === "step.running" && msg.ordering != null) {
        if (seenStepsRef.current.has(msg.ordering)) return;
        seenStepsRef.current.add(msg.ordering);
        append({
          id: `step-${msg.ordering}-running`,
          status: "running",
          text: `${pad(msg.command ?? "?", 22)}  …`,
        });
      } else if (msg.type === "step.done" && msg.ordering != null) {
        append({
          id: `step-${msg.ordering}-done`,
          status: msg.status === "ok" ? "ok" : "fail",
          text: `${pad(msg.command ?? "?", 22)}  ${msg.output ?? ""}${
            typeof msg.durationMs === "number" ? `  (${msg.durationMs}ms)` : ""
          }`,
        });
      } else if (msg.type === "run.finished") {
        if (msg.outcome === "success") {
          setBanner({ kind: "success", text: "KEY VALIDATED SUCCESSFULLY" });
        } else {
          setBanner({
            kind: "failure",
            text: msg.failureReason ?? "VALIDATION FAILED",
          });
        }
        es.close();
      }
    });
    es.addEventListener("snapshot", () => {
      // Snapshot is already covered by the REST fetch above — ignore here.
    });
    es.onerror = () => {
      // EventSource auto-reconnects; do nothing aggressive.
    };
    return () => {
      es.close();
    };
  }, [id, runId]);

  const finished = banner != null;

  const titleSuffix = useMemo(
    () => (finished ? " · done" : " · running"),
    [finished],
  );

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/service-keys"
          className="text-sm text-text-secondary hover:text-text-primary"
        >
          ← Back to service keys
        </Link>
        <h1 className="mt-2 text-2xl font-semibold">
          Validation run #{runId}
        </h1>
        <p className="mt-1 text-sm text-text-secondary">
          The terminal is server-driven. The key will be activated
          automatically iff every step passes.
        </p>
      </div>
      {error ? (
        <div className="card border-red-500/30 bg-red-500/10 text-red-300">
          {error}
        </div>
      ) : null}
      <MacTerminal
        title={`dr-validate — key=${id}${titleSuffix}`}
        lines={lines}
        banner={banner}
      />
    </div>
  );
}

function stepToLine(s: ValidationStep): TerminalLine {
  const status =
    s.status === "ok"
      ? "ok"
      : s.status === "fail"
        ? "fail"
        : s.status === "running"
          ? "running"
          : "info";
  return {
    id: `step-${s.ordering}-${s.status}`,
    status,
    text: `${pad(s.command, 22)}  ${s.output}${
      s.durationMs != null ? `  (${s.durationMs}ms)` : ""
    }`,
  };
}

function pad(s: string, n: number): string {
  if (s.length >= n) return s;
  return s + " ".repeat(n - s.length);
}
